import { LotacaoEditableTableModel } from 'components/form/editable-table/LotacaoEditableTable'
import { Cidadao } from 'graphql/types.generated'

import { CidadaoGrupoItemModel, LotacaoGrupoItemModel } from './componentes/AtividadeColetivaGrupoSelectField'
import { ParticipanteEditableTableModel } from './componentes/participantes/ParticipanteEditableTable'
import { ACOES, TipoAcaoGrupo } from './grupos/model-atividadeColetivaGrupos'
import {
  AtividadeColetivaReuniaoModel,
  AtividadeColetivaSaudeModel,
  AtividadePseModel,
  ParticipanteModel,
} from './model-atividadeColetiva'

type CidadaoNome = Pick<Cidadao, 'nome' | 'nomeSocial'>

export const getNomeCidadao = (model?: Partial<CidadaoNome>) => model?.nomeSocial ?? model?.nome

export const sortByNomeCidadao = (a: { cidadao: CidadaoNome }, b: { cidadao: CidadaoNome }) =>
  getNomeCidadao(a.cidadao).localeCompare(getNomeCidadao(b.cidadao))

const getNomeParticipante = (model: ParticipanteModel) =>
  getNomeCidadao(model.cidadao) ?? model.cidadaoParticipante.nome ?? 'ZZZ'

const getDataNascimentoParticipante = (model: ParticipanteModel) =>
  new Date(model.cidadao?.dataNascimento ?? model.cidadaoParticipante?.dataNascimento).getTime()

const compareDataNascimento = (a: ParticipanteModel, b: ParticipanteModel) => {
  const dataA = getDataNascimentoParticipante(a)
  const dataB = getDataNascimentoParticipante(b)
  return dataA < dataB ? 1 : dataA > dataB ? -1 : 0
}

export const sortByNomeAndIdadeParticipante = (a: ParticipanteModel, b: ParticipanteModel) =>
  10 * getNomeParticipante(a).localeCompare(getNomeParticipante(b)) + compareDataNascimento(a, b)

export const hasDataNascimentoAfterDataAtividade = (
  dataAtividade: LocalDate | Instant,
  dataNascimento: LocalDate | Instant
): boolean => !!dataNascimento && !!dataAtividade && new Date(dataNascimento) > new Date(dataAtividade)

export const isPse = (model: { atividadePse?: AtividadePseModel }): boolean =>
  model.atividadePse?.educacao || model.atividadePse?.saude

export const getLocalAtividade = (atividadeColetiva: AtividadeColetivaSaudeModel | AtividadeColetivaReuniaoModel) => {
  if (atividadeColetiva.inepLocalAtividade) {
    const { nomeEstabelecimento, dsInep } = atividadeColetiva.inepLocalAtividade
    return `INEP - ${nomeEstabelecimento ?? dsInep}`
  }

  if (atividadeColetiva.unidadeSaudeLocalAtividade) {
    const { cnes, nome } = atividadeColetiva.unidadeSaudeLocalAtividade
    return `CNES - ${nome ?? cnes}`
  }

  return atividadeColetiva.outraLocalidade
}

export const filterLotacoesFromGrupo = (
  lotacoesFromGrupo: LotacaoGrupoItemModel[],
  currentLotacoesEnvolvidas: LotacaoEditableTableModel[]
): LotacaoGrupoItemModel[] => {
  const currentLotacoesIds = new Set(currentLotacoesEnvolvidas.map((lotacaoEnvolvida) => lotacaoEnvolvida.lotacao?.id))

  return lotacoesFromGrupo.filter((lotacao) => !currentLotacoesIds.has(lotacao.id))
}

export const filterCidadaosFromGrupo = (
  cidadaosFromGrupo: CidadaoGrupoItemModel[],
  currentParticipantes: ParticipanteEditableTableModel[]
): CidadaoGrupoItemModel[] => {
  const currentCidadaosIds = new Set(currentParticipantes.map((participante) => participante.cidadao?.id))

  return cidadaosFromGrupo.filter((cidadao) => !currentCidadaosIds.has(cidadao.id))
}

export const removeLotacoesFromGrupo = (
  currentLotacoes: LotacaoEditableTableModel[],
  lotacoesFromGrupo: LotacaoGrupoItemModel[]
): LotacaoEditableTableModel[] => {
  const lotacoesFromGrupoIds = new Set(lotacoesFromGrupo.map((lotacao) => lotacao.id))
  return currentLotacoes.filter((lotacaoEnvolvida) => !lotacoesFromGrupoIds.has(lotacaoEnvolvida.lotacao?.id))
}

export const removeCidadaosFromGrupo = (
  currentParticipantes: ParticipanteEditableTableModel[],
  cidadaosFromGrupo: CidadaoGrupoItemModel[]
): ParticipanteEditableTableModel[] => {
  const cidadaosFromGrupoIds = new Set(cidadaosFromGrupo.map((cidadao) => cidadao.id))
  return currentParticipantes.filter((participante) => !cidadaosFromGrupoIds.has(participante.cidadao?.id))
}

const getMensagemGrupoBase = (
  tipoAcaoGrupo: TipoAcaoGrupo,
  nomeGrupo: string,
  qtdProfissionais: number,
  qtdParticipantes?: number
): string => {
  const { acao, singular, plural, preposicao } = ACOES[tipoAcaoGrupo]

  const itens: string[] = []

  if (qtdProfissionais > 0) {
    itens.push(qtdProfissionais === 1 ? '1 profissional' : `${qtdProfissionais} profissionais`)
  }

  if (qtdParticipantes > 0) {
    itens.push(qtdParticipantes === 1 ? '1 participante' : `${qtdParticipantes} participantes`)
  }

  const total = qtdProfissionais + (qtdParticipantes ?? 0)

  if (total === 0) {
    const alvo = qtdParticipantes === undefined ? 'nenhum profissional' : 'nenhum profissional ou participante'

    return `Vínculo com o grupo ${nomeGrupo} ${acao} com sucesso, ${alvo} foi ${singular} ${preposicao} atividade.`
  }

  const itensTexto = itens.join(' e ')
  const verbo = total === 1 ? `foi ${singular}` : `foram ${plural}`

  return `Vínculo com o grupo ${nomeGrupo} ${acao} com sucesso, ${itensTexto} ${verbo} ${preposicao} atividade.`
}

export const getMensagemGrupoAdicionado = (nomeGrupo: string, qtdProfissionais: number, qtdParticipantes?: number) =>
  getMensagemGrupoBase(TipoAcaoGrupo.ADICIONADO, nomeGrupo, qtdProfissionais, qtdParticipantes)

export const getMensagemGrupoRemovido = (nomeGrupo: string, qtdProfissionais: number, qtdParticipantes?: number) =>
  getMensagemGrupoBase(TipoAcaoGrupo.REMOVIDO, nomeGrupo, qtdProfissionais, qtdParticipantes)
